import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { mainSiteUrl } from "@/mocks/categories";
import { allArticles, type ArticleData } from "@/mocks/articles";

const siteUrl = import.meta.env.VITE_SITE_URL;

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<ArticleData | null>(null);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    const found = allArticles.find((a) => a.slug === slug) || null;
    setArticle(found);
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-32 pb-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">Article Not Found</h1>
          <p className="mt-3 text-foreground-500 text-sm">The article you are looking for does not exist.</p>
          <Link to="/" className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            Back to Homepage
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const slugify = (text: string) =>
    text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  return (
    <>
      <Navbar />
      <main>
        {/* Article Header */}
        <header className="pt-24 pb-12 md:pt-32 md:pb-16 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <nav className="flex items-center gap-2 text-foreground-400 text-xs mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600">{article.category}</span>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800 font-medium">{article.title.length > 50 ? article.title.slice(0, 50) + "..." : article.title}</span>
            </nav>

            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
              {article.category}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-foreground-500 text-base md:text-lg leading-relaxed">
              {article.excerpt}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-4 text-foreground-400 text-xs">
              <span>By <strong className="text-foreground-700">KakobuySpreadsheet Team</strong></span>
              <span>&bull;</span>
              <span>Updated: {article.date}</span>
              <span>&bull;</span>
              <span>{article.readTime}</span>
            </div>

            <div className="mt-8 rounded-2xl overflow-hidden h-56 md:h-80">
              <img
                src={article.heroImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </header>

        {/* Article Body */}
        <article className="py-12 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              {/* Table of Contents - Sidebar */}
              <aside className="lg:w-56 flex-shrink-0">
                <div className="lg:sticky lg:top-24">
                  <h4 className="font-semibold text-foreground-900 text-sm mb-3">Table of Contents</h4>
                  <nav className="space-y-1.5">
                    {article.tocItems.map((item) => (
                      <a
                        key={item}
                        href={`#${slugify(item)}`}
                        className="block text-foreground-500 hover:text-primary-600 text-xs leading-relaxed transition-colors"
                      >
                        {item}
                      </a>
                    ))}
                  </nav>

                  <div className="mt-6 pt-4 border-t border-background-200/40">
                    <Link
                      to="/"
                      className="inline-flex items-center gap-1.5 text-primary-600 hover:text-primary-700 text-xs font-medium transition-colors"
                    >
                      <i className="ri-arrow-left-line"></i>
                      Back to Homepage
                    </Link>
                  </div>
                </div>
              </aside>

              {/* Main Content */}
              <div className="flex-1 min-w-0">
                {article.sections.map((section) => (
                  <section key={section.id} id={slugify(section.heading)} className="mb-12">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                      {section.heading}
                    </h2>

                    {section.paragraphs.map((p, i) => (
                      <p key={i} className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                        {p}
                      </p>
                    ))}

                    {section.tipBox && (
                      <div className="bg-background-100 rounded-xl p-5 my-6 border border-background-200/50">
                        <p className="text-foreground-700 text-sm leading-relaxed">
                          <strong className="text-foreground-900">Pro Tip:</strong> {section.tipBox}
                        </p>
                      </div>
                    )}

                    {section.listItems && (
                      <ul className="space-y-3">
                        {section.listItems.map((item) => (
                          <li key={item.title} className="flex gap-3">
                            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <i className="ri-checkbox-circle-fill text-accent-500 text-sm"></i>
                            </div>
                            <div>
                              <strong className="text-foreground-900 text-sm">{item.title}</strong>
                              <p className="text-foreground-600 text-sm leading-relaxed mt-0.5">{item.description}</p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}

                    {section.numberedItems && (
                      <ol className="list-decimal list-inside space-y-3 text-foreground-600 text-sm md:text-base leading-relaxed">
                        {section.numberedItems.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ol>
                    )}

                    {section.stepItems && (
                      <div className="space-y-4">
                        {section.stepItems.map((item) => (
                          <div key={item.step} className="flex gap-4">
                            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 font-bold text-sm flex-shrink-0">
                              {item.step}
                            </div>
                            <div>
                              <strong className="text-foreground-900 text-sm">{item.title}</strong>
                              <p className="text-foreground-600 text-sm leading-relaxed mt-0.5">{item.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {section.categoryTags && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 my-5">
                        {section.categoryTags.map((tag) => (
                          <span key={tag} className="px-3 py-2 rounded-lg bg-background-100 text-foreground-700 text-xs font-medium border border-background-200/40 text-center">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </section>
                ))}

                {/* CTA inside article */}
                <div className="my-12 p-6 md:p-8 rounded-2xl bg-primary-50 border border-primary-200/40 text-center">
                  <h3 className="font-heading text-xl font-bold text-foreground-950 mb-2">
                    Ready to Start Shopping?
                  </h3>
                  <p className="text-foreground-600 text-sm mb-4">
                    Explore our verified Kakobuy spreadsheet and discover the best deals on sneakers, streetwear, and more.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a
                      href={mainSiteUrl}
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      Buy It Now
                      <i className="ri-arrow-right-up-line text-xs"></i>
                    </a>
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-primary-300 text-primary-700 font-medium text-sm hover:bg-primary-100/50 transition-all duration-200 whitespace-nowrap"
                    >
                      Back to Homepage
                      <i className="ri-arrow-left-line text-xs"></i>
                    </Link>
                  </div>
                </div>

                {/* Article FAQ */}
                <section id="frequently-asked-questions" className="mb-8">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-3">
                    {article.faqs.map((faq, index) => (
                      <div
                        key={index}
                        className="bg-background-100 rounded-xl border border-background-200/50 overflow-hidden"
                      >
                        <button
                          onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                          className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                          aria-expanded={openFaqIndex === index}
                        >
                          <span className="font-medium text-foreground-900 text-sm pr-4">
                            {faq.question}
                          </span>
                          <i
                            className={`ri-add-line text-foreground-400 text-lg flex-shrink-0 transition-transform duration-300 ${
                              openFaqIndex === index ? "rotate-45" : ""
                            }`}
                          ></i>
                        </button>
                        <div
                          className={`overflow-hidden transition-all duration-300 ${
                            openFaqIndex === index ? "max-h-96 pb-4" : "max-h-0"
                          }`}
                        >
                          <p className="px-5 text-foreground-600 text-sm leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </div>
          </div>
        </article>

        {/* Article Schema.org */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": article.title,
              "image": article.heroImage,
              "author": {
                "@type": "Organization",
                "name": "KakobuySpreadsheet Team"
              },
              "datePublished": article.date,
              "dateModified": article.date,
              "publisher": {
                "@type": "Organization",
                "name": "KakobuySpreadsheet",
                "url": siteUrl
              },
              "description": article.excerpt,
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": `${siteUrl}/article/${article.slug}`
              }
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}